import React, { useMemo, useCallback } from "react";
import moment from "moment";
import { lighten, makeStyles, useTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import AttachFileIcon from "@material-ui/icons/AttachFile";

import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import ptBR from "date-fns/locale/pt-BR";
import "react-big-calendar/lib/css/react-big-calendar.css";

import { taskDueDay } from "../../helpers/taskDue";
import { scheduleState, dayKey } from "../../helpers/scheduleStatus";

const locales = { "pt-BR": ptBR };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const calendarMessages = {
  next: "Próximo",
  previous: "Anterior",
  today: "Hoje",
  month: "Mês",
  week: "Semana",
  day: "Dia",
  agenda: "Agenda",
  date: "Data",
  time: "Hora",
  event: "Mensagem",
  noEventsInRange: "Nenhum agendamento neste período.",
  showMore: (total) => `+${total} mais`,
  allDay: "Dia todo",
};

// Cores do calendário por estado (ver helpers/scheduleStatus)
const STATE_CONFIG = {
  pending: { label: "Pendente", calColor: "#E07A00" },
  sent: { label: "Enviada", calColor: "#2E8B47" },
  attention: { label: "Precisa de atenção", calColor: "#D93025" },
};

// Converte a cor hex do status num fundo claro (estilo "card" do calendário,
// igual ao usado na Agenda).
const hexToRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const truncate = (text, max) =>
  !text ? "" : text.length > max ? `${text.substring(0, max)}...` : text;


const useStyles = makeStyles((theme) => ({
  "@global": {
    ".rbc-overlay": {
      zIndex: "1400 !important",
      maxHeight: "55vh !important",
      overflowY: "auto !important",
      borderRadius: "10px !important",
      boxShadow: "0 8px 32px rgba(0,0,0,0.24) !important",
      // A lib usa fundo branco fixo por padrão — sem isso, no tema escuro o
      // popup fica claro e os cards (pensados p/ fundo escuro) ficam com
      // texto branco ilegível.
      backgroundColor: `${theme.palette.background.paper} !important`,
      color: `${theme.palette.text.primary} !important`,
      border: `1px solid ${theme.palette.divider}`,
      "&::-webkit-scrollbar": { width: "4px" },
      "&::-webkit-scrollbar-thumb": { borderRadius: "4px", backgroundColor: "rgba(128,128,128,0.4)" },
    },
    ".rbc-overlay-header": {
      fontWeight: "600 !important",
      fontSize: "0.85rem !important",
      padding: "8px 12px !important",
      color: `${theme.palette.text.primary} !important`,
      borderBottomColor: `${theme.palette.divider} !important`,
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  calendarPaper: {
    flex: 1,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  calendarWrapper: {
    flex: 1,
    // Piso mínimo só para não ficar ilegível em janelas muito pequenas — o
    // <Calendar> abaixo usa height:"100%" para ocupar o espaço disponível
    // em vez de uma altura fixa, evitando a barra de rolagem permanente.
    minHeight: 420,
    "& .rbc-toolbar": {
      flexWrap: "wrap",
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(1.5),
    },
    "& .rbc-toolbar button": {
      borderRadius: 6,
      padding: "4px 10px",
      fontSize: "0.8rem",
      cursor: "pointer",
      color: theme.palette.text.primary,
      background: "transparent",
      borderColor: theme.palette.divider,
    },
    "& .rbc-toolbar button:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    "& .rbc-toolbar button:focus": {
      color: theme.palette.text.primary,
    },
    "& .rbc-toolbar button.rbc-active": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
      borderColor: theme.palette.primary.main,
    },
    "& .rbc-toolbar button.rbc-active:hover": {
      backgroundColor: theme.palette.primary.dark,
      color: "#fff",
    },
    "& .rbc-toolbar-label": {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
    "& .rbc-event": {
      borderRadius: 4,
      padding: "1px 5px",
    },
    // No mês, deixa o evento crescer em altura para caber o card de 2 linhas
    // (horário/contato e prévia da mensagem), igual à Agenda.
    "& .rbc-month-view .rbc-event": {
      height: "auto",
      padding: "2px 6px",
      whiteSpace: "normal",
    },
    "& .rbc-month-view .rbc-event-content": {
      whiteSpace: "normal",
    },
    "& .rbc-month-view .rbc-row-segment": {
      padding: "0 2px 2px",
    },
    "& .rbc-show-more": {
      // Azul do primary é pouco legível sobre o fundo escuro do calendário
      // — no dark mode usamos uma variação mais clara da mesma cor.
      color:
        theme.palette.type === "dark"
          ? lighten(theme.palette.primary.main, 0.45)
          : theme.palette.primary.main,
      fontWeight: 700,
      fontSize: "0.75rem",
    },
    "& .rbc-off-range-bg": {
      backgroundColor: theme.palette.type === "dark" ? "#1a1a1a" : "#f5f5f5",
    },
    "& .rbc-today": {
      backgroundColor: theme.palette.type === "dark" ? "#1a2744" : "#e8f4fd",
    },
    "& .rbc-header": {
      borderBottomColor: theme.palette.divider,
      color: theme.palette.text.primary,
      padding: "6px 4px",
      fontWeight: 600,
    },
    "& .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view": {
      borderColor: theme.palette.divider,
      borderRadius: 8,
    },
    "& .rbc-day-bg + .rbc-day-bg": { borderColor: theme.palette.divider },
    "& .rbc-month-row + .rbc-month-row": { borderColor: theme.palette.divider },
    "& .rbc-header + .rbc-header": { borderColor: theme.palette.divider },
    "& .rbc-time-content, & .rbc-time-header-content": { borderColor: theme.palette.divider },
    "& .rbc-label, & .rbc-time-slot": { color: theme.palette.text.secondary, fontSize: "0.75rem" },
    "& .rbc-date-cell": { color: theme.palette.text.primary },
    "& .rbc-agenda-date-cell, & .rbc-agenda-time-cell, & .rbc-agenda-event-cell": {
      color: theme.palette.text.primary,
      borderColor: theme.palette.divider,
    },
    "& .rbc-agenda-table": { borderColor: theme.palette.divider },
    "& .rbc-agenda-table thead tr th": {
      borderBottomColor: theme.palette.divider,
      color: theme.palette.text.secondary,
    },
  },
  legendRow: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: theme.spacing(1.5),
    alignItems: "center",
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    padding: theme.spacing(1, 1, 0.5),
    alignItems: "center",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
  eventCard: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.2,
    overflow: "hidden",
  },
  eventTopRow: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  eventTime: {
    fontWeight: 700,
    fontSize: "0.72rem",
    flexShrink: 0,
  },
  eventIcon: {
    fontSize: 13,
    flexShrink: 0,
  },
  eventName: {
    fontWeight: 600,
    fontSize: "0.72rem",
    flex: "1 1 auto",
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  eventBody: {
    fontSize: "0.68rem",
    opacity: 0.85,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

// Calendário de mensagens agendadas (e, opcionalmente, tarefas).
const ScheduleCalendar = ({
  schedules,
  tasks,
  showTasks,
  selectedDay,
  onSelectDay,
  onSelectSchedule,
  onCreateAt,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";

  const events = useMemo(() => {
    const scheduleEvts = schedules.map((s) => ({
      id: s.id,
      title: `${s.contact?.name || "Sem contato"}${
        s.body ? ` · ${truncate(s.body, 30)}` : ""
      }`,
      start: new Date(s.sendAt),
      end: new Date(s.sendAt),
      resource: s,
    }));
    if (!showTasks) return scheduleEvts;
    // dia todo: no dia da tarefa; com horário: no horário, com 30 min de duração
    const taskEvts = tasks
      .filter((t) => taskDueDay(t))
      .map((t) => {
        const timed = Boolean(t.hasDueTime);
        const start = timed ? new Date(t.dueDate) : taskDueDay(t);
        return {
          id: `task-${t.id}`,
          title: (t.completed ? "✓ " : "● ") + t.text,
          start,
          end: timed ? new Date(start.getTime() + 30 * 60000) : start,
          allDay: !timed,
          resource: { ...t, isTask: true },
        };
      });
    return [...scheduleEvts, ...taskEvts];
  }, [schedules, tasks, showTasks]);

  const eventStyleGetter = useCallback(
    (event) => {
      if (event.resource?.isTask) {
        const PRIORITY_COLORS = { high: "#7c3aed", medium: "#6d28d9", low: "#5b21b6" };
        return {
          style: {
            backgroundColor: PRIORITY_COLORS[event.resource.priority] || "#7c3aed",
            border: "2px dashed rgba(255,255,255,0.4)",
            borderRadius: 4,
            color: "#fff",
            fontSize: "0.72rem",
            opacity: event.resource.completed ? 0.5 : 1,
          },
        };
      }
      const color = STATE_CONFIG[scheduleState(event.resource)].calColor;
      return {
        style: {
          backgroundColor: hexToRgba(color, isDark ? 0.22 : 0.12),
          borderLeft: `3px solid ${color}`,
          borderTop: "none",
          borderRight: "none",
          borderBottom: "none",
          borderRadius: 4,
          color: theme.palette.text.primary,
          fontSize: "0.75rem",
        },
      };
    },
    [isDark, theme]
  );

  const ScheduleEvent = useCallback(
    ({ event }) => {
      const resource = event.resource || {};
      if (resource.isTask) {
        return (
          <div className={classes.eventCard}>
            <span className={classes.eventName}>{event.title}</span>
          </div>
        );
      }
      const color = STATE_CONFIG[scheduleState(resource)].calColor;
      const bodyPreview = truncate(resource.body, 32);
      return (
        <div className={classes.eventCard}>
          <span className={classes.eventTopRow}>
            <span className={classes.eventTime} style={{ color }}>
              {moment(resource.sendAt).format("HH:mm")}
            </span>
            <PersonOutlineIcon className={classes.eventIcon} style={{ color }} />
            <span className={classes.eventName}>
              {resource.contact?.name || "Sem contato"}
            </span>
            {resource.mediaName && (
              <AttachFileIcon className={classes.eventIcon} style={{ color }} />
            )}
          </span>
          {bodyPreview && <span className={classes.eventBody}>{bodyPreview}</span>}
        </div>
      );
    },
    [classes]
  );

  return (
    <>
      <div className={classes.calendarWrapper}>
        <Calendar
          localizer={localizer}
          culture="pt-BR"
          messages={calendarMessages}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "100%" }}
          selectable
          onSelectEvent={(event) => {
            if (event.resource?.isTask) return; // tarefas são só leitura aqui
            onSelectSchedule(event.resource);
          }}
          // Mês: clicar no dia mostra a lista dele ao lado. Semana/dia: arrastar
          // um horário já abre a nova mensagem naquele horário.
          onSelectSlot={({ start, action }) => {
            const monthClick = start.getHours() === 0 && start.getMinutes() === 0;
            if (monthClick || action === "doubleClick") onSelectDay(start);
            else onCreateAt(start);
          }}
          onShowMore={(evts, date) => onSelectDay(date)}
          doShowMoreDrillDown={false}
          dayPropGetter={(date) =>
            selectedDay && dayKey(date) === dayKey(selectedDay)
              ? { style: { boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}` } }
              : {}
          }
          eventPropGetter={eventStyleGetter}
          components={{ event: ScheduleEvent }}
          views={["month", "week", "day", "agenda"]}
          defaultView="month"
        />
      </div>
      <Box className={classes.legendRow}>
        <Typography variant="caption" color="textSecondary">
          Legenda:
        </Typography>
        {Object.entries(STATE_CONFIG).map(([key, cfg]) => (
          <Box key={key} display="flex" alignItems="center" style={{ gap: 6 }}>
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                backgroundColor: cfg.calColor,
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" color="textSecondary">
              {cfg.label}
            </Typography>
          </Box>
        ))}
        {showTasks && (
          <Box display="flex" alignItems="center" style={{ gap: 6 }}>
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                backgroundColor: "#7c3aed",
                display: "inline-block",
                flexShrink: 0,
                border: "2px dashed rgba(0,0,0,0.2)",
              }}
            />
            <Typography variant="caption" color="textSecondary">
              Tarefa
            </Typography>
          </Box>
        )}
        <Typography variant="caption" color="textSecondary" style={{ marginLeft: "auto" }}>
          Clique num dia para ver as mensagens dele
        </Typography>
      </Box>
    </>
  );
};

export default ScheduleCalendar;
